import {
  FETCH_TASKS,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_FAILED,
  ACCEPT_TASK,
  CANCEL_TASK,
} from "../store/types";
import store from '../store/store';
import { updateProfile } from "./authactions";
import { RequestPushMsg } from "../other/NotificationFunctions";
import { firebase } from '../config/configureFirebase';
import { onValue, get, off, push, update } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import {driverQueue} from '../other/sharedFunctions';
import { isFlatRateDriver } from '../other/flatRate';
import base64 from 'react-native-base64';
import AccessKey from '../other/AccessKey';

// Module-level mutex so two near-simultaneous Accept taps (two task cards
// for two customers booking from the same spot at the same time) can't
// both call acceptTask and end up writing the driver to two different
// bookings. Held for a few seconds after the write so RTDB has time to
// propagate the new ACCEPTED status into bookinglistdata, which is what
// the UI-level activeBookings check uses to block the second card.
//
// Lock duration is admin-tunable via `settings.acceptLockMs`. Default
// 5s suits typical 4G; admin can bump it on spotty networks where the
// propagation lag is longer, or drop it on fast networks where the
// lock unnecessarily blocks the driver. Falls back to 5000 if the
// setting is missing/invalid so existing deployments behave identically.
let acceptInFlight = false;
const ACCEPT_LOCK_DEFAULT_MS = 5000;

export const fetchTasks = () => (dispatch) => {
  // Listens to /driver_tasks/{uid} — a per-driver index maintained by
  // the `onRequestedDriverChanged` and `onBookingStatusChanged` Cloud
  // Functions in functions/index.js.
  //
  // Previously this used a single onValue() listener on
  //   query(ref('bookings'), orderByChild('status'), equalTo('NEW'))
  // — every online driver downloaded the FULL system-wide NEW-bookings
  // set on every change, then filtered client-side for ones requesting
  // them. With 100 drivers × 50 NEW bookings × ~5 KB pushes per change,
  // this was the dominant driver-side RTDB bandwidth cost.
  //
  // Now each driver listens only to their own slice. Pushes drop from
  // "all NEW × all drivers" to "this driver's tasks only" — typically
  // 1-5 records of ~1 KB each. ~50× cost reduction.
  //
  // Firebase Auth restores its user asynchronously: the app first paints
  // from the cached profile (and stays on it while offline), and a driver
  // can go online / accept the terms / switch role before then. Reading
  // auth.currentUser.uid there threw "Cannot read property 'uid' of null";
  // instead the listener attaches once Auth has the user the app shows.
  const { auth } = firebase;
  if (tasksAuthWait) { tasksAuthWait(); tasksAuthWait = null; }
  if (auth && auth.currentUser) {
    listenTasks(dispatch, auth.currentUser.uid);
    return;
  }
  if (!auth) return;
  let unsub = null;
  let settled = false;
  unsub = onAuthStateChanged(auth, (user) => {
    if (settled || !user) return; // still restoring (or signed out): keep waiting
    settled = true;
    if (tasksAuthWait === unsub) tasksAuthWait = null;
    if (unsub) unsub();
    const profile = store.getState().auth && store.getState().auth.profile;
    if (profile && profile.uid && profile.uid !== user.uid) return; // another account signed in
    listenTasks(dispatch, user.uid);
  });
  if (settled) unsub(); else tasksAuthWait = unsub;
};

// The driver's /driver_tasks/{uid} listener (see fetchTasks).
let tasksAuthWait = null;
const listenTasks = (dispatch, uid) => {
  const { driverTasksRef } = firebase;
  const ref = driverTasksRef(uid);
  off(ref);
  dispatch({
    type: FETCH_TASKS,
    payload: null,
  });
  onValue(ref, (snapshot) => {
    const data = snapshot.val(); // one tree per delivery (P2-1)
    if (data) {
      // Records are already slim and pre-filtered server-side; just attach
      // the booking id (the key) for downstream code that calls
      // singleBookingRef(task.id) etc.
      const arr = Object.keys(data).map((i) => ({ ...data[i], id: i }));
      dispatch({
        type: FETCH_TASKS_SUCCESS,
        payload: arr,
      });
    } else {
      dispatch({
        type: FETCH_TASKS_FAILED,
        payload: store.getState().languagedata.defaultLanguage.no_tasks,
      });
    }
  });
};

export const acceptTask = (task) => async (dispatch) => {
  // ── Per-process accept mutex ─────────────────────────────────────────
  // CRITICAL — prevents the double-accept bug where a driver taps two
  // simultaneous Accept buttons (or one tap fires twice due to a
  // FlatList reorder) and both bookings end up assigned to the same
  // driver. Skip-if-busy is safer than queue-and-serialize: if the
  // driver already kicked off an accept we want the second tap to
  // be a no-op, not "accept this one too in 200ms".
  // Every path that writes nothing resolves { ok: false, reason } so the screen
  // can clear its spinner at once instead of waiting out its 8 s give-up timer
  // (docs/performance/PERFORMANCE_AUDIT.md P0-2). The write path resolves
  // { ok: true }. Nothing about what is written, checked or sent changes.
  if (acceptInFlight) {
    console.warn('[acceptTask] another accept is in flight — ignoring second tap');
    return { ok: false, reason: 'busy' };
  }
  acceptInFlight = true;
  // Read the lock duration from settings each time — picks up admin
  // tuning without restart. Schedule the release even if everything
  // below throws so a transient network failure can't permanently
  // wedge the driver out of accepting.
  const settingsForLock = store.getState().settingsdata?.settings;
  const lockMs = parseInt(settingsForLock?.acceptLockMs, 10);
  const effectiveLockMs = Number.isFinite(lockMs) && lockMs > 0 ? lockMs : ACCEPT_LOCK_DEFAULT_MS;
  setTimeout(function () { acceptInFlight = false; }, effectiveLockMs);

  try {
    const { auth, trackingRef, singleUserRef, singleBookingRef } = firebase;

    if (!auth?.currentUser) {
      console.error('[acceptTask] auth.currentUser is null — aborting');
      acceptInFlight = false;
      return { ok: false, reason: 'no_auth' };
    }
    const uid = auth.currentUser.uid;

    const profileSnap = await get(singleUserRef(uid));
    if (!profileSnap.exists()) {
      console.error('[acceptTask] driver profile not found');
      acceptInFlight = false;
      return { ok: false, reason: 'no_profile' };
    }
    const profile = profileSnap.val();

    // Approval gate (settings.approvalGateEnabled, absent/false = off = no
    // change). While it is on, the rules refuse this accept for a driver who
    // is not approved, so skip the write and the customer push / SMS that
    // follow it; the screen shows the gate message (api.ownAccountBlock).
    // Same rule as ownAccountBlock in ../other/earningGate, written out here
    // because mobile-app/test/fetchTasks.test.js stubs every import of this
    // module.
    const gateSettings = store.getState().settingsdata?.settings;
    if (gateSettings && gateSettings.approvalGateEnabled === true && profile && profile.approved !== true
      && (profile.usertype === 'driver' || profile.usertype === 'customer')) {
      console.warn('[acceptTask] account not approved - accept skipped');
      acceptInFlight = false;
      return { ok: false, reason: 'not_approved' };
    }

    // ── Cheap pre-check via single-slot lock ───────────────────────────
    // Reads `users/{uid}/activeBookingId` (a few bytes) instead of
    // querying every booking the driver ever had. The server-side
    // updateBooking trigger is the authoritative claimer of this slot
    // (transactional on the server) and the clearer on terminal
    // transitions — we just consult it here to skip the round-trip
    // when we already know the driver is busy.
    //
    // If the slot is held by a DIFFERENT booking we verify the held
    // booking is still actually active before refusing. The slot can
    // go stale when the server-side release trigger misses a state
    // transition (deployment gap, transient error, or a status we
    // didn't anticipate). Without this self-heal, drivers get
    // permanently locked out of new accepts — a real production
    // failure mode we hit in v4.8.x. If the held id points to a
    // booking that's already terminal (or gone), we clear the slot
    // locally and proceed; the server-side claim transaction will
    // then re-set it for the new booking.
    const activeBookingId = profile && profile.activeBookingId;
    if (activeBookingId && activeBookingId !== task.id) {
      // Fail-OPEN default. If the staleness probe errors (network
      // blip, transient permission issue, etc.) we PROCEED with the
      // accept and let the server-side claim transaction decide.
      // Earlier this defaulted to fail-closed; that wedged drivers
      // out of all accepts on any transient probe failure, with no
      // visible feedback. The server-side double-accept guard
      // (updateBooking → ACTIVE check + rollback) is the
      // authoritative protection — this client probe is just a UX
      // cost saver, never the safety mechanism.
      let stillActive = false;
      try {
        const heldSnap = await get(singleBookingRef(activeBookingId));
        const held = heldSnap.exists() ? heldSnap.val() : null;
        // Active = slot legitimately held. Mirrors the server-side
        // ACTIVE list in updateBooking. PENDING is post-trip (awaiting
        // payment) and the driver is free at that point.
        const ACTIVE_STATUSES = ['ACCEPTED', 'ARRIVED', 'STARTED'];
        stillActive = !!held
          && ACTIVE_STATUSES.indexOf(held.status) >= 0
          && held.booking_type !== 'donkey_meter'
          && held.booking_type !== 'shared_trip';
      } catch (err) {
        console.warn('[acceptTask] staleness probe failed — proceeding, server will arbitrate:', (err && err.message) || err);
        // intentional fall-through; stillActive stays false
      }
      if (stillActive) {
        console.warn('[acceptTask] driver already holds active slot for', activeBookingId, '— refusing accept of', task.id);
        acceptInFlight = false;
        return { ok: false, reason: 'slot_held' };
      }
      // Slot is stale (held booking is gone or in a terminal state).
      // Clear it locally so the server-side claim can re-set it cleanly
      // and the upcoming session works. Fire-and-forget — if the clear
      // fails the server-side stale-recovery branch will still pick it
      // up on the next acceptance attempt.
      console.log('[acceptTask] stale activeBookingId', activeBookingId, '— clearing and proceeding with', task.id);
      update(singleUserRef(uid), { activeBookingId: null })
        .catch((err) => console.warn('[acceptTask] failed to clear stale activeBookingId:', (err && err.message) || err));
    }

    // Read-then-update pattern. We previously used runTransaction here, but
    // RN's modular firebase/database SDK does not reliably populate the
    // transaction updater's first-call cache without an active listener
    // priming it (the system-wide bookings listener was removed when we
    // moved to the slim driver_tasks projection). The updater consistently
    // received `null`, returned undefined, and aborted. `requestedDrivers`
    // acts as our optimistic lock instead — if another driver already
    // accepted, it'll be cleared and our update is a no-op on a stale id.
    const bookingSnap = await get(singleBookingRef(task.id));
    if (!bookingSnap.exists()) {
      console.warn('[acceptTask] booking not found');
      acceptInFlight = false;
      return { ok: false, reason: 'gone' };
    }
    const booking = bookingSnap.val();
    if (!booking.requestedDrivers || !booking.requestedDrivers[uid]) {
      console.warn('[acceptTask] driver not in requestedDrivers (already taken or never requested)');
      acceptInFlight = false;
      return { ok: false, reason: 'taken' };
    }

    // Flat-daily drivers keep the whole fare minus any fleet cut — the day
    // is already bought, so only the platform commission is waived. Fleet
    // drivers can be on the plan when flatRateAllowFleetDrivers is on; their
    // fleet percentage then applies to the gross fare (conv fee is 0).
    // Predicate must mirror server settlement (functions/common/flatRate.js);
    // rate>0 is part of it, so an unpriced car type stays per-trip.
    const flat = isFlatRateDriver(profile, store.getState().settingsdata.settings, store.getState().cartypes.cars);
    // Flat plan: the driver keeps the FULL fare — no platform commission
    // and no fleet cut either (fleet admins earn nothing from plan
    // drivers' trips; the daily fee is the only charge).
    const fleetCommission_fee = (!flat && profile?.fleetadmin)
      ? ((parseFloat(booking.estimate) - parseFloat(booking.convenience_fees)) * parseFloat(booking.fleet_admin_comission) / 100).toFixed(2)
      : 0;
    const driver_fee = flat
      ? parseFloat(booking.estimate).toFixed(2)
      : parseFloat(parseFloat(booking.estimate) - (parseFloat(booking.convenience_fees) + parseFloat(fleetCommission_fee))).toFixed(2);

    const updates = {
      driver: uid,
      driver_image: profile.profile_image ? profile.profile_image : "",
      car_image: profile.car_image ? profile.car_image : "",
      driver_name: profile.firstName + " " + profile.lastName,
      driver_contact: profile.mobile,
      driver_token: profile.pushToken ? profile.pushToken : '',
      vehicle_number: profile.vehicleNumber ? profile.vehicleNumber : "",
      vehicleModel: profile.vehicleModel ? profile.vehicleModel : "",
      vehicleMake: profile.vehicleMake ? profile.vehicleMake : "",
      driverRating: profile.rating ? profile.rating : "0",
      fleetCommission: fleetCommission_fee ? fleetCommission_fee : "0",
      fleetadmin: profile.fleetadmin ? profile.fleetadmin : "",
      status: "ACCEPTED",
      driver_share: driver_fee ? driver_fee : "0",
      driverDeviceId: task.driverDeviceId ? task.driverDeviceId : null,
      requestedDrivers: null,
      driverEstimates: null,
      ...(flat ? { convenience_fees: 0, flat_rate_driver: true } : {}),
    };

    await update(singleBookingRef(task.id), updates);

    // Side effects (formerly inside runTransaction's .then()).
    updateProfile({ queue: driverQueue ? false : true })(dispatch);
    RequestPushMsg(task.customer_token, {
      title: store.getState().languagedata.defaultLanguage.notification_title,
      msg: profile.firstName + store.getState().languagedata.defaultLanguage.accept_booking_request,
      screen: "BookedCab",
      params: { bookingId: task.id },
    });

    const driverLocation = store.getState().gpsdata.location;
    if (driverLocation && driverLocation.lat) {
      push(trackingRef(task.id), {
        at: new Date().getTime(),
        status: "ACCEPTED",
        lat: driverLocation.lat,
        lng: driverLocation.lng,
      });
    }

    dispatch({
      type: ACCEPT_TASK,
      payload: { task: task },
    });

    // Send Booking Accepted SMS
    const { config } = firebase;
    const settings = store.getState().settingsdata.settings;
    const host = (typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin)
      ? window.location.origin
      : `https://${config.projectId}.web.app`;
    const url = `${host}/send_booking_accepted_sms`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey)
        },
        body: JSON.stringify({
          driver_name: profile.firstName + " " + profile.lastName,
          driver_contact: profile.mobile,
          customer_contact: task.customer_contact,
          vehicle_number: task.vehicle_number,
          reference: task.reference,
          trip_start_time: task.trip_start_time,
          otp: task.otp,
        })
      });
    } catch (err) {
      console.log('[TripAcceptedSMS] Error sending SMS:', err.message);
    }
    return { ok: true };
  } catch (error) {
    console.error('[acceptTask] caught top-level error:', error);
    return { ok: false, reason: 'error' };
  }
};

export const cancelTask = (bookingId) => async (dispatch) => {
  try {
    const { auth, singleBookingRef } = firebase;

    if (!auth?.currentUser) {
      console.error('[cancelTask] auth.currentUser is null — aborting');
      return;
    }
    const uid = auth.currentUser.uid;

    // Same rationale as acceptTask: runTransaction's first-call cache is
    // null on RN modular SDK without an active listener, so we use
    // get() + update() with field-path partial writes for atomicity on
    // the bits we actually change.
    const bookingSnap = await get(singleBookingRef(bookingId));
    if (!bookingSnap.exists()) {
      console.warn('[cancelTask] booking not found');
      return;
    }
    const booking = bookingSnap.val();
    if (!booking.requestedDrivers) {
      console.warn('[cancelTask] no requestedDrivers — already taken or cancelled');
      return;
    }

    const driverCount = Object.keys(booking.requestedDrivers).length;
    const updates = {};

    if (driverCount === 1) {
      // Last requested driver is cancelling — flip the booking back to NEW
      // with no driver assignments so the dispatcher can re-broadcast.
      updates['status'] = 'NEW';
      updates['requestedDrivers'] = null;
      updates['driverEstimates'] = null;
      if (booking.driverOffers && booking.driverOffers[uid]) {
        updates[`driverOffers/${uid}`] = null;
      }
      if (booking.selectedBid && booking.selectedBid.driver === uid) {
        updates['selectedBid'] = null;
      }
      await update(singleBookingRef(bookingId), updates);

      // Targeted booking ("Waiting for <driver> to accept", switch on): the
      // booking is NOT cancelled by this Ignore - the customer can try again or
      // send the trip to nearby drivers - and the server tells them the truth
      // (target_declined_push). Checked inline: mobile-app/test/fetchTasks.test.js
      // stubs this file's imports one by one.
      const targetedSettings = store.getState().settingsdata && store.getState().settingsdata.settings;
      const targetedWait = Boolean(targetedSettings && targetedSettings.targetedWaitEnabled === true && booking.targetFlow === 1);
      if (!targetedWait) {
        RequestPushMsg(booking.customer_token, {
          title: store.getState().languagedata.defaultLanguage.notification_title,
          msg: store.getState().languagedata.defaultLanguage.booking_cancelled + bookingId,
          screen: "BookedCab",
          params: { bookingId: bookingId },
        });
      }
      dispatch({
        type: CANCEL_TASK,
        payload: { uid: uid, bookingId: bookingId },
      });
    } else {
      // Multiple drivers still in queue — just remove this driver's slot.
      updates[`requestedDrivers/${uid}`] = null;
      if (booking.driverEstimates && booking.driverEstimates[uid]) {
        updates[`driverEstimates/${uid}`] = null;
      }
      if (booking.driverOffers && booking.driverOffers[uid]) {
        updates[`driverOffers/${uid}`] = null;
      }
      if (booking.selectedBid && booking.selectedBid.driver === uid) {
        updates['selectedBid'] = null;
      }
      await update(singleBookingRef(bookingId), updates);
    }
  } catch (error) {
    console.error('[cancelTask] error:', error);
  }
};
